/**
 * English message catalog — the source of truth. Every user-facing string in
 * the journal lives here under a dotted, page-namespaced key. fa.ts / ar.ts
 * mirror these keys; a missing translation falls back to the English value.
 *
 * Keep keys sorted by namespace block. `{var}` placeholders are interpolated
 * by translate() in ./index.tsx.
 */
export const en: Record<string, string> = {
  // ── App chrome / navigation ──────────────────────────────────────────
  "app.name": "FinPip Journal",
  "nav.dashboard": "Dashboard",
  "nav.trades": "Trades",
  "nav.wallet": "Wallet",
  "nav.journey": "Journey",
  "nav.calendar": "Calendar",
  "calendar.title": "Trading calendar",
  "calendar.prevMonth": "Previous month",
  "calendar.nextMonth": "Next month",
  "calendar.today": "Today",
  "calendar.netPl": "Net P/L",
  "calendar.tradesCount": "{count} trades",
  "calendar.nTrades": "{count} trades",
  "calendar.greenRed": "Green / red days",
  "calendar.greenRedSub": "Days closed up vs down",
  "calendar.deposits": "Deposits",
  "calendar.withdrawals": "Withdrawals",
  "calendar.tzNote": "Days are grouped in your display timezone ({tz}).",
  "calendar.dayTitle": "{date} · {wins} won, {losses} lost",
  "nav.reports": "Reports",
  "nav.assistant": "Assistant",
  "nav.import": "Import trades",
  "nav.settings": "Settings",
  "nav.profile": "Profile",
  "nav.admin": "Admin",
  "nav.signOut": "Sign out",

  // ── Top toolbar ──────────────────────────────────────────────────────
  "top.language": "Language",
  "top.timezone": "Timezone",
  "top.theme.toggle": "Toggle dark mode",
  "top.theme.light": "Light",
  "top.theme.dark": "Dark",

  // ── Shared actions / labels ──────────────────────────────────────────
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.saved": "Saved",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.back": "Back",
  "common.loading": "Loading…",
  "common.search": "Search",
  "common.all": "All",
  "common.none": "None",
  "common.details": "Details",
  "common.today": "Today",
  "common.date": "Date",
  "common.amount": "Amount",
  "common.note": "Note",
  "common.optional": "optional",
  "common.required": "required",
  "common.yes": "Yes",
  "common.no": "No",

  // ── Feedback widget ──────────────────────────────────────────────────
  "feedback.button": "Feedback",
  "feedback.title": "Send feedback",
  "feedback.subtitle": "Found a bug or have an idea? We read every message.",
  "feedback.category": "Type",
  "feedback.category.bug": "Bug",
  "feedback.category.idea": "Idea",
  "feedback.category.other": "Other",
  "feedback.message": "Your message",
  "feedback.placeholder": "What's on your mind?",
  "feedback.send": "Send feedback",
  "feedback.sending": "Sending…",
  "feedback.thanks": "Thanks! Your feedback was sent.",
  "feedback.error": "Couldn't send feedback. Please try again.",
  "feedback.tooShort": "Please write a little more.",

  // ── PWA install banner ───────────────────────────────────────────────
  "install.title": "Install FinPip Journal",
  "install.body": "Add the app to your home screen for a faster, full-screen experience that works offline.",
  "install.button": "Install app",
  "install.dismiss": "Not now",
  "install.iosHint": "Tap the Share icon, then “Add to Home Screen”.",

  // ── Admin panel ──────────────────────────────────────────────────────
  "admin.title": "Admin",
  "admin.subtitle": "Manage users and review feedback.",
  "admin.tab.users": "Users",
  "admin.tab.feedback": "Feedback",
  "admin.stats.users": "Users",
  "admin.stats.verified": "Verified",
  "admin.stats.admins": "Admins",
  "admin.stats.trades": "Trades",
  "admin.stats.feedback": "Feedback",
  "admin.stats.newFeedback": "New feedback",
  "admin.stats.signups7d": "Signups (7d)",
  "admin.users.search": "Search name or email…",
  "admin.users.col.user": "User",
  "admin.users.col.status": "Status",
  "admin.users.col.trades": "Trades",
  "admin.users.col.joined": "Joined",
  "admin.users.col.actions": "Actions",
  "admin.users.verified": "Verified",
  "admin.users.unverified": "Unverified",
  "admin.users.admin": "Admin",
  "admin.users.makeAdmin": "Make admin",
  "admin.users.revokeAdmin": "Revoke admin",
  "admin.users.verify": "Mark verified",
  "admin.users.unverify": "Mark unverified",
  "admin.users.delete": "Delete user",
  "admin.users.deleteConfirm": "Delete {name} and ALL their data? This cannot be undone.",
  "admin.users.empty": "No users found.",
  "admin.users.you": "You",
  "admin.feedback.empty": "No feedback yet.",
  "admin.feedback.status.new": "New",
  "admin.feedback.status.read": "Read",
  "admin.feedback.status.resolved": "Resolved",
  "admin.feedback.markRead": "Mark read",
  "admin.feedback.markResolved": "Resolve",
  "admin.feedback.reopen": "Reopen",
  "admin.feedback.deleteConfirm": "Delete this feedback?",
  "admin.feedback.filter.all": "All",
  "admin.error": "Something went wrong.",


  // ── addTrade ──
  "addTrade.closeTimeRequired": "Close time is required for closed trades.",
  "addTrade.title": "Log trade",
  "addTrade.buy": "Buy",
  "addTrade.sell": "Sell",
  "addTrade.symbol": "Symbol",
  "addTrade.symbolPlaceholder": "e.g. EURUSD",
  "addTrade.assetClass": "Asset class",
  "addTrade.profitLoss": "Profit / loss ($)",
  "addTrade.profitPlaceholder": "e.g. 350 or -120",
  "addTrade.status": "Status",
  "addTrade.closed": "Closed",
  "addTrade.open": "Open",
  "addTrade.openTime": "Open time",
  "addTrade.closeTimeNa": "Close time (n/a)",
  "addTrade.closeTime": "Close time",
  "addTrade.volume": "Volume (lots)",
  "addTrade.commission": "Commission ($)",
  "addTrade.swap": "Swap ($)",
  "addTrade.openPrice": "Open price",
  "addTrade.closePrice": "Close price",
  "addTrade.stopLoss": "Stop loss",
  "addTrade.takeProfit": "Take profit",
  "addTrade.notes": "Notes",

  // ── assistant ──
  "assistant.title": "Trading coach",
  "assistant.briefingTitle": "Generate today's briefing from yesterday's trades",
  "assistant.briefingPending": "Briefing…",
  "assistant.dailyBriefing": "Daily briefing",
  "assistant.resetConfirm": "Reset the conversation?",
  "assistant.newThread": "New thread",
  "assistant.emptyState": "Ask about your trades. Try \"review my last 10 trades\" or \"what's my biggest recurring mistake?\"",
  "assistant.thinking": "Thinking…",
  "assistant.inputPlaceholder": "Type a question…",
  "assistant.send": "Send",
  "assistant.settingsTitle": "OpenAI settings",
  "assistant.configured": "Configured",
  "assistant.apiKey": "API key",
  "assistant.apiKeyUnchanged": "•••••• (unchanged)",
  "assistant.model": "Model",
  "assistant.coachInstructions": "Coach instructions (optional)",
  "assistant.coachPlaceholder": "Tailor the coach's voice or focus…",
  "assistant.savedMsg": "Saved.",
  "assistant.removeKey": "Remove key",
  "assistant.keyRemoved": "Key removed.",

  // ── dash ──
  "dash.welcome": "Welcome,",
  "dash.trader": "trader",
  "dash.loadError": "Couldn't load dashboard",
  "dash.currentTier": "Current tier",
  "dash.balance": "Balance:",
  "dash.toLevel": "{pct}% to L{level}",
  "dash.toGo": "{amount} to go",
  "dash.goalProgress": "Goal progress",
  "dash.goalOf": "{pct}% of {amount}",
  "dash.thisWeek": "This week",
  "dash.thisMonth": "This month",
  "dash.winRate": "Win rate",
  "dash.tradeOne": "{count} trade",
  "dash.tradeMany": "{count} trades",
  "dash.totalCount": "{count} total",
  "dash.accountGrowth": "Account growth",
  "dash.emptyCashflow": "No cashflow yet. Add a deposit or log a trade.",
  "dash.dailyPnl": "Daily P&L",
  "dash.emptyClosedTrades": "No closed trades yet.",
  "dash.recentActivity": "Recent activity",
  "dash.seeAll": "See all",
  "dash.emptyActivity": "No activity yet. Log your first trade or deposit.",
  "dash.setupTitle": "Set your journey goal",
  "dash.setupDesc": "Levels unlock once your first deposit anchors the journey.",
  "dash.addDeposit": "Add deposit",
  "dash.setTarget": "Set target",
  "dash.netPnl": "Net P&L",
  "dash.deposit": "Deposit",
  "dash.withdrawal": "Withdrawal",
  "dash.savingChip": "Saving",
  "dash.open": "Open",

  // ── import ──
  "import.eyebrow": "Import",
  "import.title": "Broker report",
  "import.help": "Export your history from MetaTrader 5 as an .xlsx report and drop it here. We deduplicate by position ID so re-importing the same file is safe.",
  "import.chooseFile": "Choose xlsx…",
  "import.tabMt5": "MetaTrader 5",
  "import.tabMt4": "MetaTrader 4",
  "import.tabAuto": "Auto-sync",
  "import.autoHelp": "Install a small Expert Advisor in MetaTrader and your closed trades arrive here on their own — no file exports. It only reads your history; it never places or changes an order.",
  "import.autoStep1": "1. Get your API key",
  "import.autoStep1Sub": "The EA uses this to identify your account. Keep it private — anyone with it can add trades to your journal.",
  "import.autoGenerate": "Generate API key",
  "import.autoRegenerate": "Regenerate key",
  "import.autoExisting": "A key ending ···{last4} is active",
  "import.autoLastSeen": "last sync {when}",
  "import.autoNeverUsed": "never used yet",
  "import.autoRegenWarn": "Regenerating invalidates the current key — you will need to paste the new one into the EA.",
  "import.autoKeyOnce": "Copy this now. For your security it is stored hashed and cannot be shown again — you can only regenerate.",
  "import.copy": "Copy",
  "import.copied": "Copied",
  "import.autoStep2": "2. Download the EA",
  "import.autoStep2Sub": "Ready to run — just drop it in and go. No MetaEditor, nothing to compile.",
  "import.autoSourceNote": "Prefer to compile it yourself? Get the source:",
  "import.autoServiceNote": "Already running an EA? MetaTrader 5 allows only one Expert Advisor per chart. Use the Service build instead — it needs no chart at all and runs alongside your EA. (An indicator cannot do this: MetaTrader blocks network access inside indicators.)",
  "import.autoDownloadService": "Download MT5 Service (.ex5)",
  "import.autoServiceSource": "service .mq5",
  "import.autoServiceSteps": "Using the Service build? Put it in MQL5/Services instead, then in Navigator → Services right-click it → Add service, paste your key, and right-click → Start. No chart needed.",
  "import.autoDownloadMt5": "Download for MT5 (.ex5)",
  "import.autoDownloadMt4": "Download for MT4 (.ex4)",
  "import.autoStep3": "3. Install it",
  "import.autoInstall1": "In MetaTrader: File → Open Data Folder, drop the file into MQL5/Experts (or MQL4/Experts), then right-click Navigator → Refresh.",
  "import.autoInstall3": "In MetaTrader: Tools → Options → Expert Advisors → tick “Allow WebRequest for listed URL” and add:",
  "import.autoInstall4": "Drag FinPipSync onto any chart, paste your API key into the ApiKey input, and enable algo trading.",
  "import.autoSafeNote": "Re-sending is safe: trades are deduplicated by ticket, so restarts and overlapping syncs never create duplicates — and neither does importing a report for the same period.",
  "import.helpMt4": "In MetaTrader 4 open the Account History tab, right-click, choose Save as Detailed Report, and drop the .htm file here. We deduplicate by ticket so re-importing the same file is safe.",
  "import.chooseFileMt4": "Choose .htm…",
  "import.uploading": "Uploading…",
  "import.importBtn": "Import",
  "import.importSuccess": "Imported {inserted} trades · {skipped} duplicates skipped · {wallet} wallet entries.",
  "import.recentImports": "Recent imports",
  "import.noImports": "No imports yet.",
  "import.rowMeta": "{date} · {inserted} inserted, {skipped} skipped, {wallet} wallet",

  // ── journey ──
  "journey.etaHitIt": "You've hit it!",
  "journey.etaDay": "{days} day",
  "journey.etaDays": "{days} days",
  "journey.etaMonth": "~{months} month",
  "journey.etaMonths": "~{months} months",
  "journey.etaYear": "~{years} year",
  "journey.etaYears": "~{years} years",
  "journey.title": "Path to the goal",
  "journey.editTarget": "Edit target",
  "journey.setTarget": "Set target",
  "journey.currentTier": "Current tier",
  "journey.balanceLabel": "Balance:",
  "journey.anchorLabel": "Anchor:",
  "journey.step": "step",
  "journey.toLevel": "{pct}% to L{level}",
  "journey.goalProgress": "Goal progress",
  "journey.goalOf": "{pct}% of {amount}",
  "journey.streak": "Streak",
  "journey.streakDays": "{days}d",
  "journey.streakSub": "Consecutive winning days",
  "journey.streakSubEmpty": "Post a green day to start",
  "journey.lastLevelUp": "Last level-up",
  "journey.noneYet": "None yet",
  "journey.daysAgo": "{days}d ago",
  "journey.levelUpSub1": "Clear L1 to unlock your first badge",
  "journey.levelUpSubFresh": "Fresh promotion — keep the discipline",
  "journey.levelUpSubDays": "Days since last level cleared",
  "journey.etaToGoal": "ETA to goal",
  "journey.setAGoal": "Set a goal",
  "journey.trendingFlat": "Trending flat",
  "journey.avgDaily": "avg {amount}/trading day (30d)",
  "journey.targetHeading": "Journey target",
  "journey.anchorUsd": "Start balance ($)",
  "journey.anchorPlaceholder": "e.g. 1000",
  "journey.anchorHelp": "Level 0 of the ladder. Every level target is this amount compounded by the step %, so changing it rescales all levels and recomputes past trades.",
  "journey.goalUsd": "Goal ($)",
  "journey.goalPlaceholder": "e.g. 100000",
  "journey.levelStep": "Level step (%)",
  "journey.stepHelp": "Each level = this % gain on the previous. Default 10% (Bronze → Silver at L4, Gold at L8, Platinum at L16, Diamond after L26).",
  "journey.errGoal": "Goal must be positive.",
  "journey.errStep": "Step % must be between 0 and 100.",
  "journey.errAnchor": "Start balance must be greater than 0.",
  "journey.saveTarget": "Save target",
  "journey.compoundTable": "Compound table",
  "journey.colLevel": "Level",
  "journey.colTier": "Tier",
  "journey.colBalance": "Balance",
  "journey.colGain": "Level gain",
  "journey.showLess": "Show less (to L{level})",
  "journey.showToLevel": "Show to level {level}",

  // ── login ──
  "login.tagline": "Trading, distilled.",
  "login.signIn": "Sign in",
  "login.createAccount": "Create account",
  "login.welcomeBack": "Welcome back. Sign in to your journal.",
  "login.setupBlurb": "Set up your journal in under a minute.",
  "login.displayName": "Display name",
  "login.namePlaceholder": "e.g. Ertan",
  "login.email": "Email",
  "login.emailPlaceholder": "you@example.com",
  "login.password": "Password",
  "login.passwordPlaceholder": "At least 8 characters",
  "login.signInFailed": "Sign-in failed",
  "login.throttled": "Too many login attempts. Try again in {seconds}s.",
  "login.signingIn": "Signing in…",
  "login.creating": "Creating…",
  "login.passkeyEnterEmail": "Enter your email first, then use passkey.",
  "login.passkeyFailed": "Passkey sign-in failed",
  "login.orPasskey": "Or use a passkey",
  "login.passkeyTitleOk": "Sign in with a passkey registered on this account",
  "login.passkeyTitleNo": "This browser doesn't expose WebAuthn",
  "login.passkeyWaiting": "Waiting for passkey…",
  "login.passkeyButton": "Sign in with passkey",
  "login.passkeyHintOk": "Enter your email above, then tap this button.",
  "login.passkeyHintNo": "Passkeys need a modern browser + HTTPS.",
  "login.noAccount": "No account yet?",
  "login.createOne": "Create one",
  "login.haveAccount": "Already have one?",

  // ── placeholder ──
  "placeholder.comingNext": "Coming next",
  "placeholder.inBuild": "In build",
  "placeholder.defaultNote": "This page is on the queue. Wallet is live now — try that first.",

  // ── profile ──
  "profile.account": "Account",
  "profile.title": "Profile",
  "profile.details.title": "Your details",
  "profile.details.desc": "Update the name and email tied to your account. Changing your email sends a fresh verification link and marks it unverified until you confirm.",
  "profile.name": "Name",
  "profile.namePlaceholder": "Your name",
  "profile.email": "Email",
  "profile.emailPlaceholder": "you@example.com",
  "profile.verified": "Verified",
  "profile.notVerified": "Not verified yet",
  "profile.savedWithVerify": "Saved. A verification link was sent to your new email.",
  "profile.savedSimple": "Saved.",
  "profile.couldNotSave": "Could not save.",
  "profile.saveChanges": "Save changes",
  "profile.password.title": "Change password",
  "profile.password.desc": "Set a new password. You don't need to enter your current one — your active session is proof enough.",
  "profile.newPassword": "New password",
  "profile.newPasswordPlaceholder": "At least 8 characters",
  "profile.tooShort": "Must be at least 8 characters.",
  "profile.confirmPassword": "Confirm new password",
  "profile.confirmPlaceholder": "Re-enter the new password",
  "profile.mismatch": "Passwords don't match.",
  "profile.passwordUpdated": "Password updated.",
  "profile.couldNotChangePassword": "Could not change password.",
  "profile.updating": "Updating…",
  "profile.updatePassword": "Update password",

  // ── reports ──
  "reports.title": "Analytics",
  "reports.downloadPdf": "Download PDF",
  "reports.error": "Error",
  "reports.netProfit": "Net profit",
  "reports.winRate": "Win rate",
  "reports.winLoss": "{wins}W / {losses}L",
  "reports.profitFactor": "Profit factor",
  "reports.avgWin": "Avg win",
  "reports.avgLoss": "Avg loss",
  "reports.expectancy": "Expectancy / trade",
  "reports.grossPl": "Gross P/L",
  "reports.bySymbol": "By symbol",
  "reports.noSymbolData": "No symbol data yet.",
  "reports.colSymbol": "Symbol",
  "reports.colWinPct": "Win %",
  "reports.byNews": "News trading success rate",
  "reports.byNewsSub": "Trades flagged as taken on news, against everything else. Counts show wins/losses.",
  "reports.colGroup": "Group",
  "reports.colAvg": "Avg / trade",
  "reports.newsGroup": "On news",
  "reports.wins": "{count} won",
  "reports.losses": "{count} lost",
  "reports.normalGroup": "Not on news",
  "reports.colNetPl": "Net P/L",
  "reports.byDow": "By day of week",
  "reports.byHour": "By hour of day",
  "reports.bucketSummary": "{trades} trades · net {net} · {avg} per trade",
  "reports.noTradesBucket": "No trades in this period",
  "reports.monthlyPl": "Monthly P/L",
  "reports.noMonths": "No months to show yet.",

  // ── settings ──
  "settings.account": "Account",
  "settings.version": "Version",
  "settings.savedMsg": "Saved.",
  "settings.timezones": "Timezones",
  "settings.tzHelp": "Display timezone drives every date shown across the app. MT5 import timezone tells the importer what timezone the broker's exported timestamps are in — they'll be converted to UTC on insert.",
  "settings.displayTz": "Display timezone",
  "settings.displayTzHint": "Default: Asia/Dubai. Everything you see — trade times, wallet entries, dashboard — renders in this zone.",
  "settings.mt5Tz": "MT5 import (broker) timezone",
  "settings.mt5TzHint": "Default: Europe/Istanbul. Most MT5 servers run EET/GMT+3 — leave this alone unless your broker is on a different clock.",
  "settings.strategies": "Strategies",
  "settings.strategiesHelp": "Name the setups you use. Tag any trade with one or more strategies.",
  "settings.strategyNamePlaceholder": "Strategy name",
  "settings.strategyDescPlaceholder": "Short description (optional)",
  "settings.add": "Add",
  "settings.noStrategies": "No strategies yet.",
  "settings.descriptionPlaceholder": "Description",
  "settings.archived": "Archived",
  "settings.archive": "Archive",
  "settings.unarchive": "Unarchive",
  "settings.deleteStrategyConfirm": "Delete strategy \"{name}\"?",
  "settings.factors": "Factors",
  "settings.factorsHelp": "Yes/No checks for every trade (e.g. \"Followed plan\", \"Chased entry\"). Positive factors add stars when Yes; negative subtract when Yes.",
  "settings.factorPlaceholder": "e.g. Patience",
  "settings.factorPositive": "Positive (good when Yes)",
  "settings.factorNegative": "Negative (good when No)",
  "settings.noFactors": "No factors yet.",
  "settings.deleteFactorConfirm": "Delete factor \"{label}\"?",
  "settings.passkeys": "Passkeys",
  "settings.passkeysHelp": "Sign in without a password using your device's fingerprint, face ID, PIN, or a security key.",
  "settings.passkeyNamePlaceholder": "Passkey name (e.g. iPhone Face ID)",
  "settings.passkeyNameRequired": "Give the passkey a name (e.g. \"MacBook Touch ID\").",
  "settings.passkeyRegistered": "Passkey registered.",
  "settings.passkeyRegFailed": "Passkey registration failed.",
  "settings.waitingForPasskey": "Waiting for passkey…",
  "settings.registerPasskey": "Register passkey",
  "settings.noWebauthn": "Your browser doesn't expose WebAuthn. Try Chrome, Safari, Firefox, or Edge.",
  "settings.addedDate": "Added {date}",
  "settings.removePasskeyConfirm": "Remove passkey \"{name}\"?",
  "settings.noPasskeys": "No passkeys registered yet.",
  "settings.resetSuccess": "Done — deleted {trades} trades and {wallet} wallet entries. Your balance is back to zero.",
  "settings.resetFailed": "Could not reset.",
  "settings.resetConfirm": "Delete ALL trades and wallet entries? This cannot be undone.",
  "settings.dangerZone": "Danger zone",
  "settings.dangerLead": "Permanently delete",
  "settings.dangerAll": "all your trades and wallet entries",
  "settings.dangerMid": "(and the MT5 import history) for a clean slate. Your balance resets to zero. Timezones, strategies, factors and your journey goal are kept. This",
  "settings.dangerCannotUndo": "cannot be undone",
  "settings.resetPlaceholder": "Type RESET to confirm",
  "settings.resetting": "Resetting…",
  "settings.deleteAllReset": "Delete all & reset",
  "settings.publicProfile": "Public profile",
  "settings.publicProfileHelp": "Share a read-only page of your headline stats and equity curve — no email, no trade details, just the numbers. Turn it off any time and the link stops working.",
  "settings.publicProfileToggle": "Share my stats publicly",
  "settings.publicProfileOn": "Sharing is on",
  "settings.publicProfileOff": "Private",
  "settings.copyLink": "Copy link",
  "settings.copied": "Copied",
  "settings.weeklyDigest": "Weekly digest",
  "settings.weeklyDigestHelp": "Get a short email every Monday with last week's net P/L, trade count, win rate, and your best and worst trade.",
  "settings.weeklyDigestToggle": "Email me a weekly digest",
  "settings.weeklyDigestOn": "On — a summary lands every Monday",
  "settings.weeklyDigestOff": "Off",

  // ── public (shareable stats page) ──
  "public.eyebrow": "Live trading stats",
  "public.heading": "{name}'s trading journal",
  "public.subheading": "Real, verified numbers from a live FinPip trading journal.",
  "public.netPl": "Net P/L",
  "public.winRate": "Win rate",
  "public.profitFactor": "Profit factor",
  "public.trades": "Trades",
  "public.totalReturn": "Total return",
  "public.performanceIndex": "Performance index",
  "public.performanceIndexHint":
    "Time-weighted return starting at 100 — deposits and withdrawals are stripped out, so it reflects trading skill alone.",
  "public.noTrades": "No closed trades yet.",
  "public.equityCurve": "Account growth",
  "public.ctaTitle": "Track your own trades like this",
  "public.ctaBody": "FinPip Journal is a free trading journal — log trades, see your real edge, and grow with discipline.",
  "public.cta": "Start your own free trading journal",
  "public.notFoundTitle": "This profile isn't available",
  "public.notFoundBody": "The link may be incorrect, or this trader turned off public sharing.",
  "public.loadError": "Couldn't load these stats. Please try again.",

  // ── setup ──
  "setup.step.you": "Your timezone",
  "setup.step.broker": "Broker timezone",
  "setup.step.target": "Trading target",
  "setup.step.levels": "Growth per level",
  "setup.error": "Could not save your setup. Please try again.",
  "setup.welcome": "Welcome",
  "setup.welcomeName": "Welcome, {name}",
  "setup.subtitle": "Let’s set up your journal — takes a minute.",
  "setup.stepOf": "Step {current} of {total}",
  "setup.you.desc": "Times and charts across the journal are shown in your local timezone.",
  "setup.you.label": "Your timezone",
  "setup.broker.desc": "The timezone your broker/MT5 server uses. This is how imported trade times are read before converting to your timezone. Most MT5 brokers use Europe/Istanbul (EET).",
  "setup.broker.label": "Broker (MT5) timezone",
  "setup.target.desc": "Set a target account balance to work toward. Your starting balance anchors your progress (leave it blank to use your first deposit).",
  "setup.startLabel": "Starting balance (optional)",
  "setup.startPlaceholder": "e.g. 1000",
  "setup.goalLabel": "Target balance",
  "setup.goalPlaceholder": "e.g. 10000",
  "setup.levels.desc": "Your journey is split into levels. Each level is reached by growing your balance by this percentage — a compounding milestone. 1% is a steady, sustainable step.",
  "setup.pctLabel": "Growth percentage per level",
  "setup.finish": "Finish",
  "setup.next": "Next",

  // ── trade ──
  "trade.notFound": "Trade not found.",
  "trade.grossLine": "gross {gross} · commission −{commission} · swap {swap}",
  "trade.quality": "Quality",
  "trade.starsAuto": "{value} / 5 auto",
  "trade.timing": "Timing",
  "trade.open": "Open",
  "trade.close": "Close",
  "trade.duration": "Duration",
  "trade.volumeLots": "Volume (lots)",
  "trade.prices": "Prices",
  "trade.stopLoss": "Stop loss",
  "trade.takeProfit": "Take profit",
  "trade.balanceAfter": "Balance after",
  "trade.sizeClass": "Size class",
  "trade.strategies": "Strategies",
  "trade.manage": "Manage",
  "trade.emptyStrategies": "Create strategies in Settings first, then tag trades here.",
  "trade.factors": "Factors",
  "trade.emptyFactors": "Create factors in Settings, then answer Yes/No per trade.",
  "trade.positive": "Positive",
  "trade.negative": "Negative",
  "trade.yes": "Yes",
  "trade.no": "No",
  "trade.notes": "Notes",
  "trade.newsTrading": "News trading",
  "trade.newsTradingLabel": "This trade was taken on news",
  "trade.newsTradingHelp": "Tick if the position was opened around a scheduled release or high-impact news.",
  "trade.saved": "Saved.",
  "trade.notesPlaceholder": "What worked, what didn't…",
  "trade.saveNotes": "Save notes",

  // ── trades ──
  "trades.journal": "Journal",
  "trades.logTrade": "Log trade",
  "trades.open": "Open",
  "trades.closed": "Closed",
  "trades.pnlAny": "P/L: any",
  "trades.profit": "Profit",
  "trades.loss": "Loss",
  "trades.searchPlaceholder": "Symbol or notes…",
  "trades.sortNewest": "Newest first",
  "trades.sortOldest": "Oldest first",
  "trades.sortBiggestWin": "Biggest win",
  "trades.sortBiggestLoss": "Biggest loss",
  "trades.countTrades": "{count} trades",
  "trades.error": "Error",
  "trades.empty": "No trades yet. Log your first or import an MT5 / MT4 report.",
  "trades.levelChange": "Level before → after",
  "trades.confirmDelete": "Delete this trade?",
  "trades.selectForMerge": "Select for merge",
  "trades.openedAt": "Open",
  "trades.closedAt": "Close",
  "trades.stillOpen": "Still open",
  "trades.selectedCount": "{count} selected",
  "trades.mergeBtn": "Merge",
  "trades.mergeHint": "Pick 2 or more trades with the same symbol and side.",
  "trades.mergedBadge": "Merged ×{count}",
  "trades.mergedOf": "Merged from {count} trades",
  "trades.unmerge": "Unmerge",
  "trades.confirmUnmerge": "Split this merged trade back into its original trades?",
  "trades.openTradeAria": "Open trade {id}",

  // ── tz ──
  "tz.change": "Change",
  "tz.useSystem": "Use my device timezone",
  "tz.searchPlaceholder": "Search timezones…",
  "tz.noMatches": "No matches.",
  "tz.showing": "Showing 200 of {total} — refine your search",

  // ── verify ──
  "verify.linkError": "That verification link was invalid or expired. Send a fresh one below.",
  "verify.resendError": "Could not resend the email. Try again shortly.",
  "verify.subtitle": "One quick step to go.",
  "verify.title": "Confirm your email",
  "verify.sentTo1": "We sent a confirmation link to ",
  "verify.sentTo2": ". Click it to activate your journal, then come back here.",
  "verify.spam": "Can’t find it? Check your spam folder, or resend below.",
  "verify.sent": "A fresh link is on its way.",
  "verify.checking": "Checking…",
  "verify.confirmed": "I’ve confirmed — continue",
  "verify.sending": "Sending…",
  "verify.resend": "Resend confirmation email",

  // ── wallet ──
  "wallet.title": "Cash flow",
  "wallet.recalculate": "Recalculate",
  "wallet.addEntry": "Add entry",
  "wallet.balance": "Balance",
  "wallet.netDeposited": "Net deposited",
  "wallet.inOut": "{in} in / {out} out",
  "wallet.realReturn": "Real return",
  "wallet.roi": "ROI",
  "wallet.roiNa": "You've withdrawn more than you deposited",
  "wallet.saved": "+ {amount} saved",
  "wallet.inAccount": "In account {amount}",
  "wallet.savedTotal": "Saved {amount}",
  "wallet.cashflowOverTime": "Cashflow over time",
  "wallet.entries": "Entries",
  "wallet.totalCount": "{count} total",
  "wallet.loadError": "Couldn't load entries",
  "wallet.empty": "No entries yet. Add your first deposit to anchor the journey.",
  "wallet.savingChip": "Saving",
  "wallet.confirmDelete": "Delete this entry?",
  "wallet.newEntry": "New entry",
  "wallet.type": "Type",
  "wallet.deposit": "Deposit",
  "wallet.withdrawal": "Withdrawal",
  "wallet.amountUsd": "Amount ($)",
  "wallet.amountPlaceholder": "e.g. 1000",
  "wallet.when": "When",
  "wallet.markSavingPre": "Mark as",
  "wallet.savingWord": "saving",
  "wallet.markSavingPost": "(doesn't count as loss)",
  "wallet.notes": "Notes",
  "wallet.amountError": "Amount must be greater than 0.",
  "wallet.edit": "Edit entry",
  "wallet.editEntry": "Edit entry",
  "wallet.amount": "Amount ($)",
  "wallet.dateTime": "Date & time",
  "wallet.dateError": "Pick a date and time.",
};
